// نمایش رکوردها داخل پنجره‌ی گفتگو.
//
// عمداً جدول ساده و افقی‌اسکرول است، نه یک TableContainer کامل:
// پنجره باریک است و هدف، «دیدن سریع» است نه کار کردن روی داده.
// برای کار کامل، لینک «باز کردن صفحه» زیر هر پاسخ هست.

import React from "react";
import { QueryResult } from "./lib/datasets";
import { toPersianDigits } from "./lib/text";

interface Props {
    result: QueryResult;
}

const cell = (result: QueryResult, row: any, key: string): string => {
    const column = result.columns.filter((c) => c.key === key)[0];
    if (!column) return "—";
    const value = row[key];
    return column.format ? column.format(value, row) : String(value === null || value === undefined || value === "" ? "—" : value);
};

const RecordView: React.FC<Props> = ({ result }) => {
    if (!result.rows.length && !result.detail) {
        return (
            <div className="mt-2 text-xs text-slate-500 dark:text-zink-200">
                رکوردی برای نمایش نیست.
            </div>
        );
    }

    return (
        <div className="mt-3 space-y-3">
            {/* ---------- کارت جزئیات (وقتی نتیجه دقیقاً یکی است) ---------- */}
            {result.detail && (
                <div className="p-3 border rounded-lg border-slate-200 bg-slate-50 dark:border-zink-500 dark:bg-zink-600/40">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {result.detail.map((item) => (
                            <div key={item.label} className="min-w-0">
                                <div className="text-[10px] text-slate-400 dark:text-zink-300">
                                    {item.label}
                                </div>
                                <div className="text-xs font-medium break-words text-slate-700 dark:text-zink-50">
                                    {toPersianDigits(item.value)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ---------- جدول ---------- */}
            {result.rows.length > 0 && !result.detail && (
                <div className="overflow-x-auto border rounded-lg border-slate-200 dark:border-zink-500">
                    <table className="min-w-full text-[11px] whitespace-nowrap">
                        <thead className="bg-slate-100 dark:bg-zink-600">
                            <tr>
                                {result.columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className="px-2 py-1.5 font-semibold text-right text-slate-500 dark:text-zink-200"
                                    >
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {result.rows.map((row, index) => (
                                <tr
                                    key={row.id ?? index}
                                    className="border-t border-slate-100 dark:border-zink-600"
                                >
                                    {result.columns.map((column) => (
                                        <td
                                            key={column.key}
                                            className="px-2 py-1.5 text-slate-700 dark:text-zink-100"
                                            title={cell(result, row, column.key)}
                                        >
                                            <span className="inline-block max-w-[12rem] overflow-hidden text-ellipsis align-bottom">
                                                {toPersianDigits(cell(result, row, column.key))}
                                            </span>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-400 dark:text-zink-300">
                <span>
                    نمایش {toPersianDigits(result.rows.length)} از {toPersianDigits(result.total)} رکورد
                </span>
                {result.note && <span>· {toPersianDigits(result.note)}</span>}
            </div>
        </div>
    );
};

export default RecordView;
